/**
 * Which bridge this contest talks to.
 *
 * ── Why the app has to ask ──────────────────────────────────────────────────
 * The bridge is being replaced per CONTEST: named contests move to the Laravel
 * app (api-v2), everyone else stays on Lumen until it retires. This app cannot
 * make that call itself — it picks its API base before the first request, and
 * all it holds then is the token from its URL (or from the embed's
 * <meta name="url">). The pilot is defined by contest_id, and the token is not
 * one: a contest is reachable by both its url_token and its slug. So it asks
 * the contest admin app, which owns the contests table and can resolve
 * token -> contest_id.
 *
 * The answer is a URL, not a contest id: the pilot list lives on the server, so
 * widening or aborting it is a config change there rather than a deploy of this
 * bundle — which goes live for every customer domain at once.
 *
 * ── Resolved before anything else runs ──────────────────────────────────────
 * index.js awaits this before the first render, so every request in a session
 * goes to ONE host. That matters beyond tidiness: a session that registered or
 * logged in against one bridge and then called the other would be carrying a
 * member id / token the second may not accept.
 * Cached per token in localStorage, so only a first visit pays the round trip.
 *
 * ── Failing safe ────────────────────────────────────────────────────────────
 * Every failure — network, timeout, malformed answer, an unrecognised URL —
 * falls back to the default bridge, which is where every contest runs today.
 * The worst outcome of this whole mechanism going wrong is the status quo.
 */
import axios from "axios";

/*
 * The bridge every contest used before this existed, and the fallback for
 * anything that goes wrong.
 *
 * Overridden by REACT_APP_BRIDGE_URL, deliberately NOT by REACT_APP_API_URL:
 * this repo's local .env already sets that key to a placeholder
 * (https://your-api-url.com) left over from the project template. Nothing read
 * it before, so it was inert; honouring it here would have pointed every
 * request in a local build at a host that does not exist.
 */
export const DEFAULT_BASE =
  process.env.REACT_APP_BRIDGE_URL ||
  (process.env.NODE_ENV === "production"
    ? "https://bridge.flexifunnels.com/api"
    : "http://flexiapi.local.com/api");

/** The bridge piloted contests move to. */
export const PILOT_BASE =
  process.env.REACT_APP_BRIDGE_PILOT_URL ||
  (process.env.NODE_ENV === "production"
    ? "https://api-v2.flexifunnels.com/api"
    : "http://flexiapiv2.local.com/api");

/**
 * Bases this app will accept from the resolver.
 *
 * The server is trusted, but an answer that is not one of these means something
 * is misconfigured, and silently pointing the whole app at an arbitrary origin
 * is not a failure mode worth having. Anything else falls back.
 */
const ALLOWED = [DEFAULT_BASE, PILOT_BASE];

/** How long a resolved base is reused before asking again. */
const TTL_MS = 6 * 60 * 60 * 1000;

/** Longest the first render will wait. Past this, start on the default. */
const TIMEOUT_MS = 2000;

let resolved = null;

/**
 * The contest token this page is for — the same value FeContestPage derives as
 * `id`, settled the same way and in the same order, because the base has to be
 * picked for the contest the app is about to load.
 *
 *   /:cid             -> the route param (standalone)
 *   /qr/:id           -> the QR route's param, not the literal "qr"
 *   /                 -> no param, so the embed's <meta name="url"> last segment
 *
 * useParams() is not available here: this runs before the router mounts.
 */
export function contestTokenFromPage() {
  if (typeof window === "undefined") return null;

  const segments = String(window.location.pathname || "")
    .split("/")
    .filter(Boolean);

  // /qr/<token> — "qr" is the route, the token is behind it.
  if (segments[0] === "qr") return segments[1] || null;
  if (segments[0]) return segments[0];

  // Embedded: the host page declares the contest URL and the token is its last
  // segment. Same tag and same parse as FeContestPage's getIdFromMeta().
  try {
    const meta = document.querySelector('meta[name="url"]');
    const url = meta && meta.getAttribute("content");
    const last = url && url.split("/").filter(Boolean).pop();
    return last || null;
  } catch (e) {
    return null;
  }
}

const cacheKey = (token) => `contest_api_base:${token}`;

function readCache(token) {
  try {
    const raw = localStorage.getItem(cacheKey(token));
    if (!raw) return null;
    const { base, at } = JSON.parse(raw);
    if (!base || Date.now() - at > TTL_MS) return null;
    return ALLOWED.includes(base) ? base : null;
  } catch (e) {
    return null; // private mode, blocked storage, corrupt entry
  }
}

function writeCache(token, base) {
  try {
    localStorage.setItem(cacheKey(token), JSON.stringify({ base, at: Date.now() }));
  } catch (e) {
    /* nothing to do — the next load just asks again */
  }
}

/** The base to use right now. Always a usable URL, resolved or not. */
export function apiBase() {
  return resolved || DEFAULT_BASE;
}

/** Test seam. */
export function resetApiBase() {
  resolved = null;
}

/**
 * Settle which bridge to use. Safe to call more than once; the first answer
 * wins for the life of the page, because changing host mid-session is exactly
 * what this is designed to avoid.
 */
export async function resolveApiBase(adminBase) {
  if (resolved) return resolved;

  const token = contestTokenFromPage();
  if (!token) return DEFAULT_BASE; // nothing identifies a contest yet

  const cached = readCache(token);
  if (cached) {
    resolved = cached;
    return resolved;
  }

  try {
    const res = await axios.post(
      `${adminBase}/contest-api-base`,
      { contest_token: token },
      { timeout: TIMEOUT_MS }
    );
    const base = String(res?.data?.api_base || "");
    if (ALLOWED.includes(base)) {
      resolved = base;
      writeCache(token, base);
      return resolved;
    }
  } catch (e) {
    /* fall through to the default — see the note at the top */
  }

  return DEFAULT_BASE;
}
